import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { WarningCircle, CircleNotch, UserPlus, House } from '@phosphor-icons/react';
import { CustomerFormData } from '@/lib/types';
import { CustomerSource, CustomerStatus, PhoneType, isValidPhoneType, addressHasStreetNumber, STREET_NUMBER_REQUIRED_MESSAGE } from '@ruta/shared';
import { Checkbox } from '@/components/ui/checkbox';
import { geocodeAddress } from '@/lib/mapUtils';
import { useOrgServiceRegionCenter } from '@/hooks/useOrgServiceRegionCenter';
import { PhoneTypeSelect } from '@/components/map/MapEntitySheet/ContactManagementSection';

/**
 * The property this customer is being started at, when a home has changed hands
 * (RUT-1455). Present only in "new owner" mode: the address is the property's and
 * cannot be edited, and the measurements come across with it.
 */
export interface PropertySourceCustomer {
  id: string;
  displayName: string;
  address: string;
}

interface CreateCustomerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: CustomerFormData;
  onFormDataChange: (updates: Partial<CustomerFormData>) => void;
  onSave: () => Promise<void>;
  saving: boolean;
  error: string | null;
  /** Set to start a new owner at an existing customer's property (RUT-1455). */
  propertySource?: PropertySourceCustomer;
}

const statusOptions: { value: CustomerStatus; label: string }[] = [
  { value: CustomerStatus.PROSPECT, label: 'Prospect' },
  { value: CustomerStatus.CONTACTED, label: 'Contacted' },
  { value: CustomerStatus.QUOTED, label: 'Quoted' },
  { value: CustomerStatus.ACTIVE, label: 'Active' },
  { value: CustomerStatus.INACTIVE, label: 'Inactive' },
  { value: CustomerStatus.SUSPENDED, label: 'Suspended' },
];

const sourceOptions: { value: CustomerSource; label: string }[] = [
  { value: CustomerSource.MANUAL, label: 'Manual Entry' },
  { value: CustomerSource.RATE_REQUEST, label: 'Rate Request' },
  { value: CustomerSource.REFERRAL, label: 'Referral' },
  { value: CustomerSource.MARKETING, label: 'Marketing' },
];

export function CreateCustomerModal({
  open,
  onOpenChange,
  formData,
  onFormDataChange,
  onSave,
  saving,
  error,
  propertySource,
}: CreateCustomerModalProps) {
  const addressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Bias the address geocode to the org's service region so the stored
  // coordinates land locally instead of out-of-state (RUT-3034).
  const proximityCenter = useOrgServiceRegionCenter();
  const [allowPartialAddress, setAllowPartialAddress] = useState(false);

  useEffect(() => {
    // In "new owner" mode the property already knows where it is — the server
    // copies its coordinates across, so there is nothing to geocode (RUT-1455).
    if (propertySource) return;
    if (formData.address && !formData.coordinates) {
      if (addressTimeoutRef.current) {
        clearTimeout(addressTimeoutRef.current);
      }
      addressTimeoutRef.current = setTimeout(async () => {
        const coords = await geocodeAddress(formData.address, { proximity: proximityCenter });
        if (coords) {
          onFormDataChange({
            coordinates: [coords.lng, coords.lat] as [number, number]
          });
        }
      }, 1000);
    }
    return () => {
      if (addressTimeoutRef.current) {
        clearTimeout(addressTimeoutRef.current);
      }
    };
  }, [formData.address, formData.coordinates, onFormDataChange, proximityCenter, propertySource]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave();
  };

  const hasPhone = !!formData.phone?.trim();
  const missingStreetNumber = !propertySource && !!formData.address.trim() && !addressHasStreetNumber(formData.address);
  // A mobile number is required, and any number must carry a label before it can
  // be saved (RUT-2555). Two exceptions in "new owner" mode: the address is the
  // property's so it is never typed in, and the office often starts the record
  // from the closing paperwork before it has the buyer's number — leaving it blank
  // also lets the buyer claim the record themselves when they sign in (RUT-1455).
  const isValid =
    formData.firstName?.trim() &&
    formData.lastName?.trim() &&
    (propertySource ? true : formData.address.trim()) &&
    (propertySource || !missingStreetNumber || allowPartialAddress) &&
    (propertySource ? true : hasPhone) &&
    (!hasPhone || isValidPhoneType(formData.phoneType));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl p-0 gap-0 overflow-hidden">
        <DialogHeader variant="bordered">
          <DialogTitle className="flex items-center gap-2">
            {propertySource ? <House className="h-5 w-5" /> : <UserPlus className="h-5 w-5" />}
            {propertySource ? 'New Owner at This Property' : 'New Customer'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {propertySource
              ? 'The property and its measurements carry over. Nothing about the current customer does.'
              : 'Add a customer to your account'}
          </p>
        </DialogHeader>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-6">
            {error && (
              <Alert className="border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800">
                <WarningCircle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800 dark:text-orange-200">{error}</AlertDescription>
              </Alert>
            )}

            {/* Name Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-stone-700 dark:text-stone-300 font-medium">
                  First Name <span className="text-orange-600">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName || ''}
                  onChange={(e) => onFormDataChange({ firstName: e.target.value })}
                  placeholder="First name"
                  className="bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 focus-visible:ring-stone-500"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-stone-700 dark:text-stone-300 font-medium">
                  Last Name <span className="text-orange-600">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName || ''}
                  onChange={(e) => onFormDataChange({ lastName: e.target.value })}
                  placeholder="Last name"
                  className="bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 focus-visible:ring-stone-500"
                  required
                />
              </div>
            </div>

            {/* Address — fixed to the property when starting a new owner (RUT-1455) */}
            <div className="space-y-2">
              <Label className="text-stone-700 dark:text-stone-300 font-medium">
                Address {!propertySource && <span className="text-orange-600">*</span>}
              </Label>
              {propertySource ? (
                <div className="rounded-lg border border-border bg-stone-50 dark:bg-stone-900 px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <House className="h-4 w-4 mt-0.5 shrink-0 text-stone-500" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-700 dark:text-stone-300 break-words">
                        {propertySource.address}
                      </p>
                      <p className="text-xs text-stone-500 mt-0.5">
                        Measurements copied from {propertySource.displayName}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <AddressAutocomplete
                  id="address"
                  placeholder="Enter property address"
                  value={formData.address}
                  onChange={(value) => {
                    setAllowPartialAddress(false);
                    onFormDataChange({ address: value, allowPartialAddress: false });
                  }}
                  required
                />
              )}
              {missingStreetNumber && (
                <div className="space-y-2">
                  <Alert className="border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800">
                    <WarningCircle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-800 dark:text-orange-200">
                      {STREET_NUMBER_REQUIRED_MESSAGE}
                    </AlertDescription>
                  </Alert>
                  <label className="flex items-start gap-2 text-xs text-stone-600">
                    <Checkbox
                      checked={allowPartialAddress}
                      onCheckedChange={(checked) => {
                        const next = checked === true;
                        setAllowPartialAddress(next);
                        onFormDataChange({ allowPartialAddress: next });
                      }}
                    />
                    <span>Save without a house number</span>
                  </label>
                </div>
              )}
            </div>

            {/* Contact Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-700 dark:text-stone-300 font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => onFormDataChange({ email: e.target.value })}
                  placeholder="email@example.com"
                  className="bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 focus-visible:ring-stone-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-stone-700 dark:text-stone-300 font-medium">
                  Phone {!propertySource && <span className="text-orange-600">*</span>}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => onFormDataChange({ phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="flex-1 bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 focus-visible:ring-stone-500"
                    required={!propertySource}
                  />
                  <PhoneTypeSelect
                    value={formData.phoneType}
                    onChange={(phoneType) => onFormDataChange({ phoneType })}
                    triggerClassName="w-[104px] shrink-0 bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 focus:ring-stone-500"
                  />
                </div>
                {hasPhone && !isValidPhoneType(formData.phoneType) && (
                  <p className="text-xs text-orange-600">Choose a label for this number.</p>
                )}
                {propertySource && !hasPhone && (
                  <p className="text-xs text-stone-500">
                    Optional — leave blank if you don’t have the new owner’s number yet.
                  </p>
                )}
              </div>
            </div>

            {/* Status & Source Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status" className="text-stone-700 dark:text-stone-300 font-medium">
                  Status
                </Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: CustomerStatus) => onFormDataChange({ status: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 focus:ring-stone-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="source" className="text-stone-700 dark:text-stone-300 font-medium">
                  Source
                </Label>
                <Select
                  value={formData.source}
                  onValueChange={(value: CustomerSource) => onFormDataChange({ source: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 focus:ring-stone-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Referral source */}
            <div className="space-y-2">
              <Label htmlFor="referrerSource" className="text-stone-700 dark:text-stone-300 font-medium">
                Referral source
              </Label>
              <Input
                id="referrerSource"
                value={formData.referrerSource || ''}
                onChange={(e) => onFormDataChange({ referrerSource: e.target.value })}
                placeholder="e.g. Neighbor, Google, …"
                className="bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 focus-visible:ring-stone-500"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-stone-700 dark:text-stone-300 font-medium">
                Notes
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => onFormDataChange({ notes: e.target.value })}
                placeholder="Additional notes..."
                className="bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 focus-visible:ring-stone-500 min-h-[80px] resize-none"
                rows={3}
              />
            </div>
          </DialogBody>

          <DialogFooter variant="bordered">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isValid || saving}
              className="min-w-[120px]"
            >
              {saving ? (
                <>
                  <CircleNotch className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                propertySource ? "Start New Owner" : "Create Customer"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
